import React, { useRef, useState } from "react";

import "easymde/dist/easymde.min.css";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import AdvertisingOfferPlanList from "./AdvertisingOfferPlanList";
import AdvertisingTranslations from "./AdvertisingTranslations";
import { hasUnsyncedAdvertisingUpload } from "./advertisingVideoState";
import { useUploadQueue } from "../../contexts/UploadQueue";
import "../../css/admin/utils/item-status.scss";
import { StatusEnum } from "../../types";
import ItemStatus from "../../utils/ItemStatus";
import SwitchOnOff from "../common/tools/SwitchOnOff";
import ContainerHeader from "../utils/ContainerHeader";
import RadioSelect from "../utils/RadioSelect";

import type { JSX } from "react";

import type { AdvertisingTranslationsHandle } from "./AdvertisingTranslations";
import type { IAdvertisingSingle } from "../../types";

interface Props {
  campaign: IAdvertisingSingle;
  onSubmit: (item: IAdvertisingSingle, onSuccess?: () => void) => void;
}

function MP4CampaignForm({ campaign, onSubmit }: Props): JSX.Element {
  const { t } = useTranslation("editor-advertisings");
  const { jobs } = useUploadQueue();
  const [campaignState, setCampaignState] = useState(campaign);

  const translationsRef = useRef<AdvertisingTranslationsHandle>(null);

  const handleChangeTitle = (target: HTMLInputElement): void => {
    const updatedCampaign = {
      ...campaignState,
      label: target.value,
    };

    setCampaignState(updatedCampaign);
  };

  const handleChangeState = (enabled: boolean): void => {
    const updatedCampaign = {
      ...campaignState,
      status: enabled ? StatusEnum.ENABLED : StatusEnum.DISABLED,
    };

    setCampaignState(updatedCampaign);
  };

  const handleSkipVideo = (skip: boolean): void => {
    const updatedCampaign = {
      ...campaignState,
      skip_video: skip,
    };

    setCampaignState(updatedCampaign);
  };

  const handleSkipDuration = (
    domEvent: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const updatedCampaign = {
      ...campaignState,
      skip_video_duration: domEvent.target.value,
    };

    setCampaignState(updatedCampaign);
  };

  const handleChangeAdPos = (value: number): void => {
    const updatedCampaign = {
      ...campaignState,
      offset_percentage: value,
    };

    setCampaignState(updatedCampaign);
  };

  const handleSave = async (): Promise<void> => {
    if (hasUnsyncedAdvertisingUpload(jobs, campaign.uuid)) {
      toast.error(t("videoUpload.saveBlockedByUpload"));

      return;
    }

    const isValidForm = (await translationsRef.current?.isValid()) ?? true;

    if (!isValidForm) {
      toast.error(t("form:validationError"));

      return;
    }

    const { translations, translations_indexes: translationsIndexes } =
      translationsRef.current?.getApiTranslations?.() ?? campaignState;

    onSubmit(
      {
        ...campaignState,
        translations,
        translations_indexes: translationsIndexes,
      },
      () => translationsRef.current?.onSaveSuccess(),
    );
  };

  return (
    <div className="editor-smartlist-form-container editor-has-breadcrumbs">
      <ContainerHeader
        withBackBtn
        defaultLabel={t("unamed")}
        item={campaignState}
        itemStatus={ItemStatus.getAdvertisingStatus(campaignState)}
        onChangeLabel={handleChangeTitle}
        onChangeState={handleChangeState}
        onSave={handleSave}
      />
      <AdvertisingTranslations ref={translationsRef} campaign={campaignState} />
      <div className="editor-tile-container">
        <div className="editor-form-section">
          <div className="editor-form-section-title">{t("insertion_mode")}</div>
          <div className="editor-form-row">
            <RadioSelect
              onChange={handleChangeAdPos}
              selected={campaignState.offset_percentage}
              options={[
                {
                  key: 0,
                  value: t("pre"),
                },
                {
                  key: 50,
                  value: t("mid"),
                },
                {
                  key: 100,
                  value: t("post"),
                },
              ]}
            />
          </div>
        </div>
        <div className="editor-form-section section-separator">
          <div className="editor-form-section-title">{t("options")}</div>
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={campaignState.skip_video}
                iconName=""
                name="skip_video"
                onChange={handleSkipVideo}
                size="sm"
              />
            </div>
            <div className="editor-form-label">{t("allowSkip")}</div>
            {campaignState.skip_video ? (
              <div className="editor-form-inline">
                <div className="editor-form-label">{t("skipAfter")}</div>
                <div
                  className="editor-form-input"
                  style={{ maxWidth: "250px" }}
                >
                  <div className="editor-form-input-suffix-s">
                    <input
                      min={0}
                      onChange={handleSkipDuration}
                      type="number"
                      value={campaignState.skip_video_duration}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <AdvertisingOfferPlanList advertisingCampaign={campaignState} />
      </div>
    </div>
  );
}

export default MP4CampaignForm;
